import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"
import {
  Book,
  createBook,
  CreateBookPayload,
  updateBook,
} from "../../../service/books"
import {
  BookClassification,
  getBookClassifications,
  getBookSubClassifications,
} from "../../../service/book-classifications"
import { BOOK_CONDITIONS, CONDITIONS } from "../../../constant"

interface LibraryModalProps {
  selectedItem?: Book
  onItemAdded?: () => void
  onItemUpdated?: () => void
}
const DEFAULT_BOOK_FORM_DATA = {
  title: "",
  author: "",
  publisher: "",
  isbn: "",
  quantity: 0,
  classificationCode: "",
  condition: CONDITIONS.NEW,
  cabinetNo: "",
  shelfNo: "",
  price: 0,
}

const LibraryModal: React.FC<LibraryModalProps> = ({
  selectedItem = null,
  onItemAdded = () => {},
  onItemUpdated = () => {},
}) => {
  const [loading, setLoading] = useState(false)

  const [classificationsLoading, setClassificationsLoading] = useState(false)
  const [classifications, setClassifications] = useState<BookClassification[]>(
    [],
  )
  const [subClassificationsLoading, setSubClassificationsLoading] =
    useState(false)
  const [subClassifications, setSubClassifications] = useState<
    BookClassification[]
  >([])

  const [bookFormData, setBookFormData] = useState<CreateBookPayload>(
    DEFAULT_BOOK_FORM_DATA,
  )

  const fetchBookClassifications = async () => {
    try {
      setClassificationsLoading(true)
      const res = await getBookClassifications()
      if (res?.success) {
        setClassifications(res.data)
      }
    } catch (err) {
      console.error("Error fetching classifications:", err)
    } finally {
      setClassificationsLoading(false)
    }
  }

  const fetchBookSubClassifications = async (code: string) => {
    try {
      setSubClassificationsLoading(true)
      const res = await getBookSubClassifications(code)
      if (res?.success) {
        const divisions = res.data?.divisions || []
        setSubClassifications(divisions)
      } else {
        setSubClassifications([])
      }
    } catch (err) {
      setSubClassifications([])
    } finally {
      setSubClassificationsLoading(false)
    }
  }

  useEffect(() => {
    fetchBookClassifications()
  }, [])

  useEffect(() => {
    if (bookFormData.classificationCode) {
      fetchBookSubClassifications(bookFormData.classificationCode)
    }
  }, [bookFormData.classificationCode])

  useEffect(() => {
    if (selectedItem) {
      setBookFormData(selectedItem)
    } else {
      resetForm()
    }
  }, [selectedItem])

  const handleBookInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target
    setBookFormData(prev => ({
      ...prev,
      [name]: value, // Store all values as strings initially
    }))
  }

  const resetForm = () => setBookFormData(DEFAULT_BOOK_FORM_DATA)

  const handleSubmit = async (e: React.FormEvent, isEdit: boolean = false) => {
    e.preventDefault()

    if (loading) return

    setLoading(true)

    try {
      if (
        !bookFormData.quantity ||
        isNaN(bookFormData.quantity) ||
        bookFormData.quantity <= 0
      ) {
        toast.error("Quantity must be a positive number")
        setLoading(false)
        return
      }

      if (isNaN(bookFormData.price) || bookFormData.price < 0) {
        toast.error("Price must be greater than or equal to 0")
        setLoading(false)
        return
      }

      // DDC: main classification required; sub depends on main (disabled until main selected)
      if (!bookFormData.classificationCode) {
        toast.error("Please select a main classification")
        setLoading(false)
        return
      }

      if (!bookFormData.subClassificationCode) {
        toast.error("Please select a sub-classification")
        setLoading(false)
        return
      }

      if (isEdit && selectedItem) {
        const response = await updateBook(selectedItem._id, {
          ...bookFormData,
        })
        if (response.success) {
          toast.success("Book updated successfully!")
          onItemUpdated()
          closeModal("edit_library_book")
        } else {
          toast.error("Failed to update book")
        }
      } else {
        const response = await createBook({
          ...bookFormData,
        })
        if (response.success) {
          toast.success("Book added successfully!")
          onItemAdded()
          closeModal("add_library_book")
          resetForm()
        } else {
          toast.error("Failed to add book")
        }
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Error saving book. Please try again."
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const closeModal = (modalId: string) => {
    const el = document.getElementById(modalId)
    if (el) {
      el.setAttribute("data-bs-dismiss", "modal")
      el.click()
      el.removeAttribute("data-bs-dismiss")
    }
  }

  const handleCloseModal = (isEdit: boolean = false) => {
    if (!isEdit) {
      resetForm()
    }
  }

  const renderBookForm = (isEdit: boolean = false) => (
    <form onSubmit={e => handleSubmit(e, isEdit)}>
      <div className="modal-body">
        <div className="row">
          <div className="col-md-12">
            {/* DDC classification */}
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Main Classification *</label>
                  <select
                    className="form-control"
                    value={bookFormData.classificationCode}
                    onChange={handleBookInputChange}
                    required
                    disabled={classificationsLoading}
                    name="classificationCode"
                  >
                    <option value="">
                      {classificationsLoading
                        ? "Loading..."
                        : "Select main classification"}
                    </option>
                    {classifications.map(c => (
                      <option key={c.code} value={c.code}>
                        {c.code} - {c.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Sub-Classification</label>
                  <select
                    className="form-control"
                    value={bookFormData.subClassificationCode}
                    onChange={handleBookInputChange}
                    name="subClassificationCode"
                    disabled={
                      !bookFormData.classificationCode ||
                      subClassificationsLoading
                    }
                  >
                    <option value="">
                      {!bookFormData.classificationCode
                        ? "Select main classification first"
                        : subClassificationsLoading
                          ? "Loading..."
                          : "Optional: select sub-classification"}
                    </option>
                    {subClassifications.map(d => (
                      <option key={d.code} value={d.code}>
                        {d.code} - {d.title}
                      </option>
                    ))}
                  </select>
                  <div className="form-text">
                    {bookFormData.classificationCode
                      ? `Selected: ${bookFormData.classificationCode}`
                      : ""}
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Book Condition *</label>
                  <select
                    className="form-control"
                    name="condition"
                    value={bookFormData.condition}
                    onChange={handleBookInputChange}
                    required
                  >
                    {BOOK_CONDITIONS.map(condition => (
                      <option key={condition.value} value={condition.value}>
                        {condition.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Book Price *</label>
                  <input
                    type="number"
                    className="form-control"
                    name="price"
                    value={bookFormData.price}
                    onChange={handleBookInputChange}
                    required
                    placeholder="Enter price"
                  />
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Book Name *</label>
              <input
                type="text"
                className="form-control"
                name="title"
                value={bookFormData.title}
                onChange={handleBookInputChange}
                required
                placeholder="Enter Book Name"
              />
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">ISBN (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="isbn"
                    value={bookFormData.isbn}
                    onChange={handleBookInputChange}
                    placeholder="Enter ISBN (optional)"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Cabinet No</label>
                  <input
                    type="text"
                    className="form-control"
                    name="cabinetNo"
                    value={bookFormData.cabinetNo}
                    onChange={handleBookInputChange}
                    placeholder="Enter cabinet number"
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Shelf No</label>
                  <input
                    type="text"
                    className="form-control"
                    name="shelfNo"
                    value={bookFormData.shelfNo}
                    onChange={handleBookInputChange}
                    placeholder="Enter shelf number"
                  />
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Publisher *</label>
              <input
                type="text"
                className="form-control"
                name="publisher"
                value={bookFormData.publisher}
                onChange={handleBookInputChange}
                required
                placeholder="Enter Publisher"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Author *</label>
              <input
                type="text"
                className="form-control"
                name="author"
                value={bookFormData.author}
                onChange={handleBookInputChange}
                required
                placeholder="Enter Author"
              />
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Quantity *</label>
                <input
                  type="number"
                  className="form-control"
                  name="quantity"
                  value={bookFormData.quantity}
                  onChange={handleBookInputChange}
                  required
                  placeholder="Enter Quantity"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-light me-2"
          data-bs-dismiss="modal"
          onClick={() => handleCloseModal(isEdit)}
        >
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading
            ? isEdit
              ? "Updating..."
              : "Adding..."
            : isEdit
              ? "Save Changes"
              : "Add Book"}
        </button>
      </div>
    </form>
  )

  return (
    <>
      {/* Add Book Modal */}
      <div className="modal fade" id="add_library_book">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Book</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => handleCloseModal(false)}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            {renderBookForm(false)}
          </div>
        </div>
      </div>

      {/* Edit Book Modal */}
      <div className="modal fade" id="edit_library_book">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Book</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            {renderBookForm(true)}
          </div>
        </div>
      </div>
    </>
  )
}

export default LibraryModal
